import { useGetEvent } from 'api/events/events.api'
import { useEffect, useMemo, useState } from 'react'
import { EventPost, initializeSettingInstructionsWithIds } from '../events.helper'

/**
 * What the open modal holds, from the point of view of incoming server data: `editing` carries
 * changes the user has not saved, `saving` carries a write the user has already dispatched.
 */
export type EventPostModalEditState = 'clean' | 'editing' | 'saving'

type AppliedEventPostRevision = {
  appliedEventPost: EventPost | null
  isFetching: boolean
  hasWithheldServerUpdate: boolean
  setModalEditState: (editState: EventPostModalEditState) => void
  adoptCreatedEventPost: (createdEventPost: EventPost) => void
  /** Changes exactly when the modal must rebuild its local state from `appliedEventPost`. */
  appliedRevisionKey: string
}

function withSettingInstructionIds(eventPost: EventPost): EventPost {
  if (eventPost.setting_instructions?.linked_objects) {
    return {
      ...eventPost,
      setting_instructions: initializeSettingInstructionsWithIds(eventPost.setting_instructions),
    }
  }

  return { ...eventPost }
}

/**
 * The event revision the open modal is built from, which is not always the newest one the server
 * has. The modal re-initialises its whole local state from this object, so applying a revision
 * that arrives mid-edit discards what the user typed and unmounts the button they just clicked —
 * with no request, no error and no visible change. A newer revision of the same event is therefore
 * withheld until the modal reports it holds nothing unsaved.
 */
export function useAppliedEventPost(selectedEventPostId?: number | null): AppliedEventPostRevision {
  // A modal opened to create an event has no id to follow until its create returns, and without
  // one it never sees another user's save of that event, so it cannot withhold or warn about it.
  const [createdEventPost, setCreatedEventPost] = useState<EventPost | null>(null)
  const eventPostId = selectedEventPostId ?? createdEventPost?.id ?? null
  const { data: selectedEventPost, isFetching } = useGetEvent(eventPostId)

  // conventions-allow: manual-memo — a stable reference, not a performance measure: the effect below
  // writes this object into state and depends on it, so a fresh one each render re-renders forever.
  const initializedSelectedEventPost = useMemo(
    () => (selectedEventPost ? withSettingInstructionIds(selectedEventPost) : null),
    [selectedEventPost]
  )

  const [appliedEventPost, setAppliedEventPost] = useState<EventPost | null>(initializedSelectedEventPost)
  const [appliedEventPostId, setAppliedEventPostId] = useState(eventPostId)
  const [modalEditState, setModalEditState] = useState<EventPostModalEditState>('clean')

  if (appliedEventPostId !== eventPostId) {
    setAppliedEventPostId(eventPostId)
    setAppliedEventPost(initializedSelectedEventPost)
    setModalEditState('clean')
  }

  useEffect(() => {
    if (appliedEventPostId !== eventPostId) return
    if (modalEditState !== 'clean') return

    // A save invalidates the event query without waiting for the refetch, so between the write
    // landing and the new revision arriving the cache still holds the revision the user just
    // overwrote. Applying it there would put the other user's values back into the dialog.
    if (isFetching) return

    // The query reports no data while a refetch is in flight after a cache reset; taking that at
    // face value tore the open dialog down and dropped a click the user had already made.
    if (!initializedSelectedEventPost) return

    setAppliedEventPost(initializedSelectedEventPost)
  }, [initializedSelectedEventPost, appliedEventPostId, eventPostId, modalEditState, isFetching])

  const hasWithheldServerUpdate =
    modalEditState === 'editing' &&
    !!appliedEventPost &&
    !!initializedSelectedEventPost &&
    initializedSelectedEventPost.updated_at !== appliedEventPost.updated_at

  // The create response is the revision the user saw saved. Starting from it, rather than waiting
  // for the first fetch, means a save by someone else in between is still measured against it.
  function adoptCreatedEventPost(created: EventPost): void {
    if (created.id === undefined || created.id === null) return

    const createdRevision = withSettingInstructionIds(created)
    setCreatedEventPost(createdRevision)
    setAppliedEventPostId(created.id)
    setAppliedEventPost(createdRevision)
  }

  // The modal already holds the revision it created, so that one must not rebuild it: a rebuild
  // replays the dialog's entry, drops focus and resets everything the user had expanded.
  const isOwnCreatedRevision = !!createdEventPost && appliedEventPost?.updated_at === createdEventPost.updated_at
  const appliedRevisionKey =
    appliedEventPost?.id !== undefined && appliedEventPost?.id !== null && !isOwnCreatedRevision
      ? `event-${appliedEventPost.id}-${appliedEventPost.updated_at ?? appliedEventPost.id}`
      : `event-${selectedEventPostId ?? 'new'}`

  return {
    appliedEventPost,
    isFetching,
    hasWithheldServerUpdate,
    setModalEditState,
    adoptCreatedEventPost,
    appliedRevisionKey,
  }
}
